import { msg } from "../locales/translate";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import Config from "../Config";
import { GD } from "../GD";
import { buildSessionTerminationDebug, readStoredAuthToken, TSessionTerminationDebug } from "./sessionDebug";
import { withClientListFinance } from "./clientListRequest";

type TSessionRecoveryResult = "recovered" | "logged_out" | "failed";

const AUTH_LOGOUT_ERROR_CODE = 6;
const API_VERSION_TWO = 2;
const SESSION_COMPROMISED_CODE = 1003;
const UNAUTHORIZED_STATUS = 401;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

class HTTP {
	busy: boolean = false;
	sessionRecoveryPromise: Promise<TSessionRecoveryResult> | null = null;
	hasShownSessionCompromisedToast = false;

	constructor() {
		//axios.defaults.maxRedirects=0
		GD.REQ_AXIOS.listener = async req => this.request(req);
		GD.REQ_API.listener = async req => this.requestAPI(req);
		GD.S_HTTP_BUSY.onSubscribe = cb => {
			cb!(this.busy);
		};
	}

	async requestAPI(req: IAPIRequest): Promise<IAPIResponse> {
		const apiURL = this.getAPIURL(req);
		const config = await this.createAPIRequestConfig(req, apiURL);
		let result = await this.request(config);

		if (!req.skipSessionRecovery && this.shouldRecoverSession(result)) {
			const recoveryResult = await this.tryRecoverSession(apiURL, this.getSessionRecoveryError(result), {
				failedRequestMethod: req.method,
				failedRequestToken: this.extractBearerToken(config),
			});

			if (recoveryResult === "recovered") {
				result = await this.request(await this.createAPIRequestConfig(req, apiURL));
			} else if (recoveryResult === "logged_out") {
				return {
					error: { code: UNAUTHORIZED_STATUS, message: "SESSION_TERMINATED" },
				};
			}
		}

		if (result.error) {
			if (result.error.code === AUTH_LOGOUT_ERROR_CODE) GD.S_AUTH_LOG_OUT.invoke();
			return { error: result.error };
		}

		if (result.data && typeof result.data === "object" && "error" in result.data && result.data.error) {
			return { error: result.data.error };
		}

		if (result.data === null || result.data === undefined)
			return {
				error: { code: -1, message: msg("emptyResponse") },
			};

		if (req.method === "auth/login") {
			this.hasShownSessionCompromisedToast = false;
		}

		return { data: result.data };
	}

	private getAPIURL(req: IAPIRequest): string {
		let apiURL = Config.scheme.URL_API;

		if (window.location.hostname !== "localhost" && !window.location.hostname.includes("crm.ask")) {
			apiURL = `${window.location.origin}/api/v2/`;
		}

		if (req.apiVersion === API_VERSION_TWO) {
			apiURL = Config.scheme.URL_API;
		}

		return apiURL;
	}

	private async createAPIRequestConfig(req: IAPIRequest, apiURL: string): Promise<AxiosRequestConfig> {
		const url = req.url ?? apiURL + req.method;
		const headers = { ...(req.headers ?? {}) };
		const currentProject = await GD.REQ_CURRENT_PROJECT.request();
		if (req.expectedProjectId !== undefined && currentProject?.id !== req.expectedProjectId) {
			throw new Error(msg("projectChangedReopenTheUserInTheSelectedProject"));
		}

		if (req.excludeAuth === undefined) {
			const token = await GD.REQ_AUTH_TOKEN.request();
			if (token) {
				headers["Authorization"] = `Bearer ${token}`;
			}
		}

		if (currentProject?.id !== undefined && currentProject?.id !== null) {
			headers["x-project-id"] = currentProject.id.toString();
		}

		let data = req.data;
		if (req.method === "clients/list") {
			const profile = await GD.REQ_PROFILE.request();
			const roles = profile?.projects.find(project => project.id === currentProject?.id)?.roles ?? currentProject?.roles;
			data = withClientListFinance(isRecord(data) ? data : {}, roles);
		}
		if (isRecord(data) && !(data instanceof FormData)) {
			data = this.clearMarkup(JSON.parse(JSON.stringify(data)));
		}

		let endData: FormData | Record<string, unknown> | undefined = data instanceof FormData || isRecord(data) ? data : undefined;
		if (req.files && req.files.length) {
			endData = this.jsonToFormData(isRecord(data) ? data : {});
			headers["Content-Type"] = "multipart/form-data";
			for (const f of req.files) endData.append("files", f);
		}

		return {
			url,
			method: req.httpMethod ?? "POST",
			data: endData,
			headers,
			responseType: req.responseType ?? "json",
			timeout: req.timeoutMs,
			signal: req.signal,
		};
	}

	private shouldRecoverSession(result: IHTTPResponse): boolean {
		return result.error?.code === SESSION_COMPROMISED_CODE || result.data?.error?.code === SESSION_COMPROMISED_CODE;
	}

	private getSessionRecoveryError(result: IHTTPResponse): IError | undefined {
		return result.error ?? result.data?.error;
	}

	private extractBearerToken(config: AxiosRequestConfig): string | null {
		const auth = config.headers?.["Authorization"];
		return typeof auth === "string" && auth.startsWith("Bearer ") ? auth.slice("Bearer ".length) : null;
	}

	private async tryRecoverSession(apiURL: string, initialError?: IError, debug?: Partial<TSessionTerminationDebug>): Promise<TSessionRecoveryResult> {
		if (!this.sessionRecoveryPromise) {
			this.sessionRecoveryPromise = this.performSessionRecovery(apiURL, initialError, debug).finally(() => {
				this.sessionRecoveryPromise = null;
			});
		}

		return this.sessionRecoveryPromise;
	}

	private async performSessionRecovery(apiURL: string, initialError?: IError, debug?: Partial<TSessionTerminationDebug>): Promise<TSessionRecoveryResult> {
		const token = await GD.REQ_AUTH_TOKEN.request();
		const fingerprint = await GD.REQ_AUTH_FINGERPRINT.request();

		if (!token || !fingerprint) {
			return "failed";
		}

		const recovery = await this.request({
			url: `${apiURL}auth/session/recover`,
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				ignoreBusy: "true",
			},
			data: {
				token,
				fingerprint,
			},
			responseType: "json",
		});

		if (recovery.status === UNAUTHORIZED_STATUS) {
			this.handleForcedLogout(initialError?.details ? initialError : recovery.error, {
				...debug,
				recoveryToken: token,
				recoveryStatus: recovery.status ?? null,
				fingerprint,
			});
			return "logged_out";
		}

		if (recovery.error) {
			return "failed";
		}

		const nextToken = this.extractRecoveredToken(recovery.data);
		if (!nextToken) {
			return "failed";
		}

		this.hasShownSessionCompromisedToast = false;
		GD.S_AUTH_TOKEN_REPLACED.invoke(nextToken);
		return "recovered";
	}

	private extractRecoveredToken(payload: unknown): string | null {
		if (typeof payload === "string") {
			return payload;
		}

		if (payload && typeof payload === "object" && "token" in payload && typeof payload.token === "string") {
			return payload.token;
		}

		return null;
	}

	private handleForcedLogout(error?: IError, debug?: Partial<TSessionTerminationDebug>): void {
		// localStorage is wiped by S_AUTH_LOG_OUT, so read the stored token first
		const storedToken = readStoredAuthToken();

		GD.S_AUTH_LOG_OUT.invoke();

		if (this.hasShownSessionCompromisedToast) {
			return;
		}

		this.hasShownSessionCompromisedToast = true;
		GD.S_SESSION_TERMINATED_POPUP.invoke({
			message: msg("yourSessionWasTerminatedPleaseSignInAgain"),
			details: {
				server: error?.details ?? error?.message ?? null,
				debug: buildSessionTerminationDebug(debug ?? {}, storedToken),
			},
		});
	}

	clearMarkup(data: unknown): unknown {
		if (isRecord(data)) {
			for (const k in data) {
				if (k.startsWith("$__")) {
					delete data[k];
					continue;
				}
				if (isRecord(data[k])) data[k] = this.clearMarkup(data[k]);
			}
		}
		return data;
	}

	async request(req: AxiosRequestConfig): Promise<IHTTPResponse> {
		let ignoreBusy = false;

		if (req.headers && req.headers["ignoreBusy"]) {
			ignoreBusy = true;
			delete req.headers["ignoreBusy"];
		}

		if (!ignoreBusy) {
			GD.S_HTTP_BUSY.invoke(true);
			this.busy = true;
		}

		let response: AxiosResponse | null = null;
		try {
			response = await axios(req);
		} catch (e) {
			const err = e as AxiosError<{
				data?: IAPIResponse | null;
				error: IError;
			}>;

			this.busy = false;
			GD.S_HTTP_BUSY.invoke(false);

			if (err.response && err.response.data) {
				if (err.response.data.error) {
					return {
						error: err.response.data.error,
						headers: err.response.headers,
						status: err.response.status,
						data: err.response.data.data,
					};
				}
			}

			return {
				error: { code: -1, message: err.message, httpStatus: err.response?.status,
					networkError: !err.response && ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(err.code ?? "") },
				headers: err.response?.headers,
				status: err.response?.status,
				data: null,
			};
		}

		this.busy = false;
		GD.S_HTTP_BUSY.invoke(false);

		if (!response) {
			return {
				error: { code: -2, message: msg("noResponse") },
				status: 0,
				data: null,
			};
		}

		if (response.data && typeof response.data === "object" && "responseTime" in response.data && "version" in response.data) {
			if (response.data.error) {
				return {
					headers: response.headers,
					status: response.status,
					error: response.data.error,
					data: null,
				};
			} else {
				return {
					headers: response.headers,
					status: response.status,
					data: response.data.data,
				};
			}
		}

		return {
			headers: response.headers,
			status: response.status,
			data: response.data,
		};
	}

	jsonToFormData(data: Record<string, unknown>, formData = new FormData(), parentKey = ""): FormData {
		for (const key in data) {
			if (!Object.prototype.hasOwnProperty.call(data, key)) continue;

			const value = data[key];
			const fieldKey = parentKey ? `${parentKey}[${key}]` : key;

			if (value instanceof File) {
				formData.append(fieldKey, value);
			} else if (value instanceof Blob) {
				formData.append(fieldKey, value);
			} else if (isRecord(value)) {
				this.jsonToFormData(value, formData, fieldKey);
			} else {
				formData.append(fieldKey, String(value));
			}
		}
		return formData;
	}
}

export default HTTP;
